import React, { useState, useEffect } from "react";
import { CheckCircle, AlertCircle, CircleX } from "lucide-react";
import CustomHeading from "./common/CustomHeading";
import CustomInput from "./common/CustomInput";
import Button from "./common/Button";
import {
  saveCreditEligibilitySection,
  getCibilScoreDataByUserIdLoanId,
  getDbrDetailsByUserId
} from "../ActionCreator";
import Loader from "../../../../../presentationals/Loader/Loader.component";
import { Input } from "@mui/material";

const textarea = {
  width: "100%",
  border: "1.5px solid #cbd5e1",
  borderRadius: "10px",
  padding: "10px 12px",
  fontSize: "13px",
  color: "#0f172a",
  outline: "none",
  background: "#fff",
  boxSizing: "border-box",
  resize: "vertical",
  minHeight: "90px",
  lineHeight: "1.6",
  fontFamily: "inherit",
  transition: "all 0.15s ease",
};

const BRE_ROWS = [
  {
    rule: "Bureau Score",
    expected: ">= 600",
    value: ">697",
    fieldName: "breBureauScoreResult",
    valueField: "bureauScoreValue",
  },
  {
    rule: "Overdue Amount",
    expected: "<= 1000",
    value: "<-1020",
    fieldName: "breOverdueAmountResult",
    valueField: "overdueAmountValue",
  },
  {
    rule: "Business Vintage",
    expected: ">= 12",
    value: ">= 12 Months",
    fieldName: "creditBusinessVintage",
    valueField: "businessVintageValue",
  },
  {
    rule: "DPDs in last 3 months",
    expected: "<= 0",
    value: "<-0",
    fieldName: "breDpds3MonthsResult",
    valueField: "dpds3MonthsValue",
  },
  {
    rule: "DPDs in last 6 months",
    expected: "< 25",
    value: "< 25",
    fieldName: "breDpds6MonthsResult",
    valueField: "dpds6MonthsValue",
  },
  {
    rule: "DPDs in last 12 months",
    expected: "<55",
    value: "<55",
    fieldName: "breDpds12MonthsResult",
    valueField: "dpds12MonthsValue",
  },
  {
    rule: "DPDs in last 24 months",
    expected: "< 85",
    value: "< 85",
    fieldName: "breDpds24MonthsResult",
    valueField: "dpds24MonthsValue",
  },
  {
    rule: "Settled/WOF/LOS/DBI in last 5 years",
    expected: "<= 1",
    value: "<= 1",
    fieldName: "breSettled5YearsResult",
    valueField: "settled5YearsValue",
  },
  {
    rule: "Loans taken in last 3 months",
    expected: "<= 2",
    value: "< -2",
    fieldName: "breLoans3MonthsResult",
    valueField: "loans3MonthsValue",
  },
  {
    rule: "Drop in sales in any month",
    expected: "<= 20%",
    value: ">23%",
    fieldName: "breDropSalesResult",
    valueField: "dropSalesValue",
  },
  {
    rule: "Cheque bounces due to insufficient funds in last 6 months",
    expected: "<= 2",
    value: "<= 2",
    fieldName: "breChequeBouncesResult",
    valueField: "chequeBouncesValue",
  },
  {
    rule: "Actual sales (as per GST) visible in banking credits",
    expected: ">= 60%",
    value: ">60%",
    fieldName: "breActualSalesResult",
    valueField: "actualSalesValue",
  },
  {
    rule: "Additional loans found in Bank Statement",
    expected: "No",
    value: "No",
    fieldName: "breAdditionalLoansResult",
    valueField: "additionalLoansValue",
  },
  {
    rule: "PAT positive for last 2 audited financials",
    expected: "Yes",
    value: "Yes",
    fieldName: "brePatPositiveResult",
    valueField: "patPositiveValue",
  },
  {
    rule: "Current ratio in recent financial year",
    expected: ">= 1",
    value: ">= 1",
    fieldName: "breCurrentRatioResult",
    valueField: "currentRatioValue",
  },
  {
    rule: "Debt to Income Ratio",
    expected: "<= 60%",
    value: "<-60%",
    fieldName: "breDebtIncomeRatioResult",
    valueField: "debtIncomeRatioValue",
  },
  {
    rule: "Credit Enquiries in last 3 months",
    expected: "<= 6",
    value: "<= 6",
    fieldName: "breCreditEnquiriesResult",
    valueField: "creditEnquiriesValue",
  },
];

const CreditEligibilityAssessment = ({
  allDetail,
  initialData,
  showPopup,
  onRefresh,
}) => {
  const [form, setForm] = useState(() => {
    const initialState = {
      creditBusinessVintage: "",
      creditTurnover: "",
      creditProfitability: "",
      creditBureauScore: "",
      creditFoir: "",
      businessVintageMonths: "",
      annualTurnover: "",
      dscrPerLoan: "",
      profitabilityStatus: "",
      bureauScore: "",
      foirAnalysisPercent: "",
      breBureauScoreResult: "",
      breOverdueAmountResult: "",
      breDpds3MonthsResult: "",
      breDpds6MonthsResult: "",
      breDpds12MonthsResult: "",
      breDpds24MonthsResult: "",
      breSettled5YearsResult: "",
      breLoans3MonthsResult: "",
      breDropSalesResult: "",
      breChequeBouncesResult: "",
      breActualSalesResult: "",
      breAdditionalLoansResult: "",
      brePatPositiveResult: "",
      breCurrentRatioResult: "",
      breDebtIncomeRatioResult: "",
      breCreditEnquiriesResult: "",
      overallAssessmentNotes: "",
    };

    BRE_ROWS.forEach((row) => {
      initialState[row.valueField] = "";
    });

    return initialState;
  });

  const [allPass, setAllPass] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [cibilData, setCibilData] = useState(null);
  const [dbrData, setDbrData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);

  // Fetch CIBIL Score and DBR Details from BRE APIs
  useEffect(() => {
    const userId = allDetail?.userId;
    const loanId = allDetail?.loanId || allDetail?.applicationId;
    if (userId) {
      setDataLoading(true);
      getCibilScoreDataByUserIdLoanId(userId, loanId || "", (res) => {
        if (res) {
          setCibilData(res?.data || res);
        }
      });
      getDbrDetailsByUserId(userId, (res) => {
        setDataLoading(false);
        if (res) {
          setDbrData(res?.data || res);
        }
      });
    }
  }, [allDetail?.userId, allDetail?.loanId, allDetail?.applicationId]);

  // Sync state with parent-supplied initialData
  useEffect(() => {
    if (initialData) {
      setForm((prev) => {
        const updated = {
          ...prev,
          creditBusinessVintage: initialData.creditBusinessVintage ?? "",
          creditTurnover: initialData.creditTurnover ?? "",
          creditProfitability: initialData.creditProfitability ?? "",
          creditBureauScore: initialData.creditBureauScore ?? "",
          creditFoir: initialData.creditFoir ?? "",
          businessVintageMonths: initialData.businessVintageMonths ?? "",
          annualTurnover: initialData.annualTurnover ?? "",
          dscrPerLoan: initialData.dscrPerLoan ?? "",
          profitabilityStatus: initialData.profitabilityStatus ?? "",
          bureauScore: initialData.bureauScore ?? "",
          foirAnalysisPercent: initialData.foirAnalysisPercent ?? "",
          breBureauScoreResult: initialData.breBureauScoreResult ?? "",
          breOverdueAmountResult: initialData.breOverdueAmountResult ?? "",
          breDpds3MonthsResult: initialData.breDpds3MonthsResult ?? "",
          breDpds6MonthsResult: initialData.breDpds6MonthsResult ?? "",
          breDpds12MonthsResult: initialData.breDpds12MonthsResult ?? "",
          breDpds24MonthsResult: initialData.breDpds24MonthsResult ?? "",
          breSettled5YearsResult: initialData.breSettled5YearsResult ?? "",
          breLoans3MonthsResult: initialData.breLoans3MonthsResult ?? "",
          breDropSalesResult: initialData.breDropSalesResult ?? "",
          breChequeBouncesResult: initialData.breChequeBouncesResult ?? "",
          breActualSalesResult: initialData.breActualSalesResult ?? "",
          breAdditionalLoansResult: initialData.breAdditionalLoansResult ?? "",
          brePatPositiveResult: initialData.brePatPositiveResult ?? "",
          breCurrentRatioResult: initialData.breCurrentRatioResult ?? "",
          breDebtIncomeRatioResult: initialData.breDebtIncomeRatioResult ?? "",
          breCreditEnquiriesResult: initialData.breCreditEnquiriesResult ?? "",
          overallAssessmentNotes: initialData.overallAssessmentNotes ?? "",
        };

        BRE_ROWS.forEach((row) => {
          updated[row.valueField] = initialData[row.valueField] ?? "";
        });

        return updated;
      });
    }
  }, [initialData]);

  const handleChange = (e, field) => {
    const val = e.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: val,
    }));
  };

  const handleAllPass = (checked) => {
    setAllPass(checked);
    setForm((prev) => {
      let updated = { ...prev };
      breRows.forEach((row) => {
        updated[row.fieldName] = checked ? "Pass" : "";
      });
      return updated;
    });
  };

  const handleSave = () => {
    setBtnLoading(true);
    
    const payload = {
      creditBusinessVintage: form.creditBusinessVintage,
      creditTurnover: form.creditTurnover,
      creditProfitability: form.creditProfitability,
      creditBureauScore: form.creditBureauScore,
      creditFoir: form.creditFoir,
      businessVintageMonths: form.businessVintageMonths,
      annualTurnover: form.annualTurnover,
      dscrPerLoan: form.dscrPerLoan,
      profitabilityStatus: form.profitabilityStatus,
      bureauScore: form.bureauScore,
      foirAnalysisPercent: form.foirAnalysisPercent,
      breBureauScoreResult: form.breBureauScoreResult,
      breOverdueAmountResult: form.breOverdueAmountResult,
      breDpds3MonthsResult: form.breDpds3MonthsResult,
      breDpds6MonthsResult: form.breDpds6MonthsResult,
      breDpds12MonthsResult: form.breDpds12MonthsResult,
      breDpds24MonthsResult: form.breDpds24MonthsResult,
      breSettled5YearsResult: form.breSettled5YearsResult,
      breLoans3MonthsResult: form.breLoans3MonthsResult,
      breDropSalesResult: form.breDropSalesResult,
      breChequeBouncesResult: form.breChequeBouncesResult,
      breActualSalesResult: form.breActualSalesResult,
      breAdditionalLoansResult: form.breAdditionalLoansResult,
      brePatPositiveResult: form.brePatPositiveResult,
      breCurrentRatioResult: form.breCurrentRatioResult,
      breDebtIncomeRatioResult: form.breDebtIncomeRatioResult,
      breCreditEnquiriesResult: form.breCreditEnquiriesResult,
      overallAssessmentNotes: form.overallAssessmentNotes,
    };

    BRE_ROWS.forEach((row) => {
      payload[row.valueField] = form[row.valueField];
    });

    saveCreditEligibilitySection(
      allDetail?.userId,
      payload,
      (res) => {
        setBtnLoading(false);
        if (res?.success === true) {
          showPopup("Credit Eligibility Profile saved successfully");
          if (onRefresh) onRefresh();
        } else {
          showPopup("Error saving Credit Eligibility profile section");
        }
      },
      (err) => {
        setBtnLoading(false);
        showPopup("Error saving Credit Eligibility profile section");
        console.error("Error saving Credit Eligibility profile section", err);
      }
    );
  };

  const policyRows = [
    {
      parameter: "Business Vintage",
      policyNorm: "≥ 12 Months",
      deviation: "-",
      remark: "Risk setup mitigation",
      fieldName: "creditBusinessVintage",
    },
    {
      parameter: "Turnover",
      policyNorm: "-",
      deviation: "-",
      remark: "Cash Flow Based",
      fieldName: "creditTurnover",
    },
    {
      parameter: "Profitability",
      policyNorm: "Profitable",
      deviation: "-",
      remark: "Profitability Check",
      fieldName: "creditProfitability",
    },
    {
      parameter: "Bureau Score",
      policyNorm: "≥ 600",
      deviation: "< 600",
      remark: "Detailed bureau analysis",
      fieldName: "creditBureauScore",
    },
    {
      parameter: "FOIR (Fixed Obligation to Income Ratio)",
      policyNorm: "≤ 60%",
      deviation: "≥ 60%",
      remark: "Acceptable with surplus",
      fieldName: "creditFoir",
    },
  ];

  const breRows = BRE_ROWS;

  // ── shared style tokens ──────────────────────────────────────────────────
  const card = {
    backgroundColor: "#fff",
    border: "1px solid #D9D9D9",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "16px",
  };
  const sectionTitle = {
    fontSize: "16px",
    fontWeight: "500",
    color: "#854E2B",
    margin: "0 0 14px 0",
  };
  const tbl = { width: "100%", borderCollapse: "collapse", fontSize: "12px" };
  const th = {
    padding: "9px 12px",
    textAlign: "left",
    fontWeight: "500",
    fontSize: "11px",
    color: "#686C70",
    borderBottom: "1px solid #E8E8E8",
    background: "#fafafa",
  };
  const td = {
    padding: "9px 12px",
    fontSize: "12px",
    color: "#3a3a3a",
    borderBottom: "1px solid #F0F0F0",
  };
  const input = {
    width: "100%",
    border: "1px solid #D9D9D9",
    borderRadius: "6px",
    padding: "8px 10px",
    fontSize: "12px",
    color: "#0C0B10",
    backgroundColor: "#fff",
    outline: "none",
    boxSizing: "border-box",
  };
  const criteriaBox = {
    background: "#f5f5f5",
    borderRadius: "8px",
    padding: "12px 14px",
    marginTop: "12px",
  };
  const criteriaTitle = {
    fontSize: "11px",
    fontWeight: "600",
    color: "#0C0B10",
    margin: "0 0 8px 0",
  };
  const criteriaItem = {
    fontSize: "11px",
    color: "#686C70",
    margin: "4px 0",
    lineHeight: "1.5",
  };

  return (
    <div style={{ background: "#F9FAFB", marginTop: "12px" }}>
      <CustomHeading
        title="Credit Eligibility & Policy Assessment"
        subtitle="Evaluate against credit policy parameters"
        buttonText="Negative"
        icon={CircleX}
        buttonColor="#EB6062"
        borderColor="#7B3D15"
        background="#B752132B"
      />
      {/* <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        <Button
          title="Save Data"
          onClick={handleSave}
          btnLoading={btnLoading}
        />
      </div> */}

     

      {dataLoading && <Loader />}

      {/* ── Row: CIBIL & Score Card Assessment Tables ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          marginTop: "12px",
        }}
      >
        {/* CIBIL & DBR Assessment */}
        <div style={{ ...card, margin: 0 }}>
          <p style={sectionTitle}>CIBIL & DBR Assessment</p>
          <div style={{ overflowX: "auto" }}>
            <table style={tbl}>
              <thead>
                <tr>
                  <th style={{ ...th, width: "60%" }}>Parameter</th>
                  <th style={th}>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={td}>No. of Overdue Accounts</td>
                  <td style={{ ...td, fontWeight: 600 }}>{cibilData?.overDueAccount ?? "-"}</td>
                </tr>
                <tr style={{ background: "#fafafa" }}>
                  <td style={td}>Income</td>
                  <td style={{ ...td, fontWeight: 600, color: "#16a34a" }}>
                    {cibilData?.income != null && cibilData.income !== "" ? cibilData.income : "-"}
                  </td>
                </tr>
                <tr>
                  <td style={td}>Obligations</td>
                  <td style={{ ...td, fontWeight: 600, color: "#dc2626" }}>
                    {cibilData?.obligations != null && cibilData.obligations !== "" ? cibilData.obligations : "-"}
                  </td>
                </tr>
                <tr style={{ background: "#fafafa" }}>
                  <td style={td}>Surplus</td>
                  <td style={{ ...td, fontWeight: 600, color: "#2563eb" }}>
                    {cibilData?.surplus != null && cibilData.surplus !== "" ? cibilData.surplus : "-"}
                  </td>
                </tr>
                <tr>
                  <td style={td}>KCC/STL</td>
                  <td style={td}>{cibilData?.shortTermCreditCard ?? "-"}</td>
                </tr>
                <tr style={{ background: "#fafafa" }}>
                  <td style={td}>DBR</td>
                  <td style={{ ...td, fontWeight: 600 }}>
                    {cibilData?.dbr != null ? `${cibilData.dbr}` : (dbrData?.dbrpercentage ?? "-")}
                  </td>
                </tr>
                <tr>
                  <td style={td}>Remark 1</td>
                  <td style={td}>
                    {Array.isArray(cibilData?.remarksList) && cibilData.remarksList.length > 0
                      ? cibilData.remarksList[0]
                      : cibilData?.remarks || "-"}
                  </td>
                </tr>
                <tr style={{ background: "#fafafa" }}>
                  <td style={td}>Cibil Score</td>
                  <td style={{ ...td, fontWeight: 700, color: Number(cibilData?.cibilScore) >= 600 ? "#16a34a" : "#dc2626" }}>
                    {cibilData?.cibilScore ?? "-"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Score Card */}
        <div style={{ ...card, margin: 0 }}>
          <p style={sectionTitle}>Score Card</p>
          <div style={{ overflowX: "auto" }}>
            <table style={tbl}>
              <thead>
                <tr>
                  <th style={{ ...th, width: "60%" }}>Parameter</th>
                  <th style={th}>Score</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={td}>Stability</td>
                  <td style={{ ...td, fontWeight: 600 }}>{dbrData?.stability ?? 0}</td>
                </tr>
                <tr style={{ background: "#fafafa" }}>
                  <td style={td}>Debts</td>
                  <td style={{ ...td, fontWeight: 600 }}>{dbrData?.debts ?? 0}</td>
                </tr>
                <tr>
                  <td style={td}>KYC</td>
                  <td style={{ ...td, fontWeight: 600 }}>{dbrData?.kyc ?? 0}</td>
                </tr>
                <tr style={{ background: "#fafafa" }}>
                  <td style={td}>NTC</td>
                  <td style={{ ...td, fontWeight: 600 }}>{dbrData?.ntc ?? 0}</td>
                </tr>
                <tr>
                  <td style={td}>Bureau</td>
                  <td style={{ ...td, fontWeight: 600 }}>{dbrData?.bureau ?? 0}</td>
                </tr>
                <tr style={{ background: "#fafafa" }}>
                  <td style={td}>Residence</td>
                  <td style={{ ...td, fontWeight: 600 }}>{dbrData?.residence ?? 0}</td>
                </tr>
                <tr>
                  <td style={td}>Overdue Msg</td>
                  <td style={{ ...td, fontWeight: 600 }}>{dbrData?.overDueMsgsTrend ?? 0}</td>
                </tr>
                <tr style={{ background: "#fafafa" }}>
                  <td style={td}>Access</td>
                  <td style={{ ...td, fontWeight: 600 }}>{dbrData?.access ?? 0}</td>
                </tr>
                <tr>
                  <td style={td}>Corporate</td>
                  <td style={{ ...td, fontWeight: 600 }}>{dbrData?.corporate ?? 0}</td>
                </tr>
                <tr style={{ background: "#fafafa" }}>
                  <td style={td}>Multiple Pay Day</td>
                  <td style={{ ...td, fontWeight: 600 }}>{dbrData?.multiplePayday ?? 0}</td>
                </tr>
                <tr style={{ background: "#eff6ff", borderTop: "2px solid #bfdbfe" }}>
                  <td style={{ ...td, fontWeight: 700, color: "#1e40af" }}>Final Score</td>
                  <td style={{ ...td, fontWeight: 800, fontSize: "14px", color: "#1e40af" }}>
                    {dbrData?.finalScore ?? (dbrData?.scoring ?? 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Row: Business Vintage + Turnover ── */}
      {/* <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          marginTop: "12px",
        }}
      >
        

      
        <div style={{ ...card, margin: 0 }}>
          <p style={sectionTitle}>Turnover Analysis</p>
          <div>
            <CustomInput
              label={"Annual Turnover (₹ in Lakh)"}
              type="text"
              placeholder="-"
              value={form.annualTurnover}
              onChange={(e) => handleChange(e, "annualTurnover")}
            />
          </div>
         
        </div>

        <div style={{ ...card, margin: 0 }}>
          <p style={sectionTitle}>Profitability Analysis</p>
          <div>
            <CustomInput
              label={"Profitability Status"}
              type="text"
              placeholder="-"
              value={form.profitabilityStatus}
              onChange={(e) => handleChange(e, "profitabilityStatus")}
            />
          </div>
          
        </div>
       
      </div> */}

      {/* ── Row: DSCR + Profitability ── */}
      {/* <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "16px",
          marginTop: "12px",
        }}
      >
       

       

       
        <div style={{ ...card, margin: 0 }}>
          <p style={sectionTitle}>Bureau Score Analysis</p>
          <div>
          
            <label
          style={{
            display     : "block",
            marginBottom: 5,
            fontWeight  : 600,
            fontSize    : 12,
            color       : "#374151",
            letterSpacing: ".01em",
          }}
        >
          Bureau Score
        </label>
            <textarea
            placeholder="Enter Score (300-900)"
            value={form.bureauScore}
            onChange={(e) => handleChange(e, "bureauScore")}
            rows={4}
            style={textarea}
          />
          </div>
         
        </div>
      </div> */}

      
      

     
      {/* <div style={{ ...card, marginTop: "12px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <p style={sectionTitle}>Credit Policy Parameters</p>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={allPass}
              onChange={(e) => handleAllPass(e.target.checked)}
            />
            Mark All as Pass
          </label>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={tbl}>
            <thead>
              <tr>
                <th style={{ ...th, width: "40%" }}>Rule</th>
                <th style={th}>Expected Value</th>
                <th style={th}>Value</th>
                <th style={th}>Result (Manual Drop Down)</th>
              </tr>
            </thead>
            <tbody>
              {breRows.map((row, i) => (
                <tr
                  key={i}
                  style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}
                >
                  <td style={td}>{row.rule}</td>
                  <td style={td}>{row.expected}</td>
                  <td style={td}>
                    <input
                      type="text"
                      value={form[row.valueField] || ""}
                      onChange={(e) => handleChange(e, row.valueField)}
                      placeholder={row.value || "Enter value"}
                      style={{
                        width: "100%",
                        border: "1.5px solid #cbd5e1",
                        borderRadius: "6px",
                        padding: "6px 10px",
                        fontSize: "12px",
                        color: "#0f172a",
                        outline: "none",
                        background: "#fff",
                        boxSizing: "border-box",
                        transition: "all 0.15s ease",
                      }}
                    />
                  </td>
                  <td style={td}>
                    <select
                      value={form[row.fieldName]}
                      onChange={(e) => handleChange(e, row.fieldName)}
                      style={{
                        border: "1px solid #D9D9D9",
                        borderRadius: "5px",
                        padding: "4px 8px",
                        fontSize: "11px",
                        color: "#1e8a4a",
                        background: "#fff",
                        cursor: "pointer",
                        outline: "none",
                        fontWeight: "500",
                        minWidth: "80px",
                      }}
                    >
                      <option value="">Select Result</option>
                      <option value="Pass">Pass</option>
                      <option value="Fail">Fail</option>
                      <option value="Review">Review</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}
      {/* <div style={{ ...card, marginTop: "12px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <p style={sectionTitle}>Credit Policy Parameters</p>

         
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={tbl}>
            <thead>
              <tr>
                <th style={{ ...th, width: "40%" }}>Parameter</th>
                <th style={th}>Value</th>
             
              </tr>
            </thead>
            <tbody>
              {breRows?.map((row, i) => (
                <tr
                  key={i}
                  style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}
                >
                  <td style={td}>{row?.rule}</td>
                  <td style={td}>{row?.value}</td>
                  
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}


    </div>
  );
};

export default CreditEligibilityAssessment;
